import EventEmitter from 'eventemitter3';
import { deepClone } from 'src/components/#common/tree-list/deepClone';
export class TreeListNode {
    constructor(data, options) {
        // region events
        this._events = new EventEmitter();
        this._nodes = null;
        this._maxDepth = null;
        this._parent = null;
        this.data = data;
        this._options = options;
    }
    on(name, listener) {
        this._events.on(name, listener);
        return () => {
            this._events.off(name, listener);
        };
    }
    onChange() {
        this._events.emit('change');
    }
    // endregion
    // region nodes
    onChangeList() {
        this._events.emit('change-list');
        this.onChange();
    }
    checkIndex(index, min = 0, max = this.length) {
        if (index < min || index >= max) {
            throw new Error(`Index ${index} out of range [${min}, ${max})`);
        }
    }
    get length() {
        return this._nodes ? this._nodes.length : 0;
    }
    get(index) {
        this.checkIndex(index);
        return this._nodes[index];
    }
    toArray() {
        return this._nodes
            ? this._nodes.slice()
            : [];
    }
    forEach(callback) {
        if (!this._nodes) {
            return;
        }
        for (let i = 0, len = this._nodes.length; i < len; i++) {
            const node = this._nodes[i];
            if (callback.call(this, node, i)) {
                break;
            }
        }
    }
    indexOf(node) {
        let index = -1;
        this.forEach((n, i) => {
            if (n === node) {
                index = i;
                return true;
            }
            return false;
        });
        return index;
    }
    get maxDepth() {
        var _a;
        if (this._maxDepth === null) {
            this._maxDepth = ((_a = this._nodes) === null || _a === void 0 ? void 0 : _a.length)
                ? this._nodes.reduce((max, node) => Math.max(max, node.maxDepth), 0) + 1
                : 0;
        }
        return this._maxDepth;
    }
    recalcMaxDepth() {
        if (this._maxDepth === null) {
            return;
        }
        this._maxDepth = null;
        if (this.parent) {
            this.parent.recalcMaxDepth();
        }
        this.onChange();
    }
    insert(index, node) {
        this.checkIndex(index, 0, this.length + 1);
        // protect from infinite nesting
        if (node === this || node.isParentOf(this)) {
            throw new Error('Can not insert node into itself or its children');
        }
        const prevIndex = node.parent ? node.parent.indexOf(node) : -1;
        // move node if it is already in list
        if (node.parent === this) {
            this.move(prevIndex, index);
            return;
        }
        // remove node from previous parent
        if (node.parent) {
            node.parent.deleteIndex(prevIndex);
        }
        // insert node into new parent
        node.parent = this;
        if (!this._nodes) {
            this._nodes = [node];
        }
        else {
            this._nodes.splice(index, 0, node);
        }
        this.recalcMaxDepth();
        this.onChangeList();
    }
    push(node) {
        this.insert(this.length, node);
    }
    deleteIndex(index) {
        this.checkIndex(index);
        const node = this._nodes[index];
        this._nodes.splice(index, 1);
        node.parent = null;
        this.recalcMaxDepth();
        this.onChangeList();
    }
    deleteNode(node) {
        const index = this.indexOf(node);
        if (index === -1) {
            return false;
        }
        this.deleteIndex(index);
        return true;
    }
    deleteThis() {
        if (this.parent) {
            this.parent.deleteNode(this);
        }
    }
    set(index, node) {
        this.checkIndex(index, 0, this.length + 1);
        // protect from infinite nesting
        if (node === this || node.isParentOf(this)) {
            throw new Error('Can not insert node into itself or its children');
        }
        const prevIndex = node.parent ? node.parent.indexOf(node) : -1;
        if (node.parent === this && prevIndex === index) {
            return;
        }
        const parent = node.parent;
        if (parent && parent !== this) {
            parent.deleteIndex(prevIndex);
        }
        let prevNode = this._nodes ? this._nodes[index] : null;
        if (!this._nodes) {
            this._nodes = [node];
        }
        else {
            this._nodes[index] = node;
        }
        if (parent === this) {
            this._nodes.splice(prevIndex, 1);
        }
        if (prevNode) {
            prevNode.parent = null;
        }
        node.parent = this;
        this.recalcMaxDepth();
        this.onChangeList();
    }
    move(from, to) {
        this.checkIndex(from);
        this.checkIndex(to, 0, this.length + 1);
        if (from === to) {
            return;
        }
        const node = this._nodes[from];
        let increment;
        if (from < to) {
            increment = 1;
            to--;
        }
        else {
            increment = -1;
        }
        for (let i = from; i !== to; i += increment) {
            this._nodes[i] = this._nodes[i + increment];
        }
        this._nodes[to] = node;
        this.onChangeList();
    }
    // endregion
    // region parent
    onChangeParent() {
        this._events.emit('change-parent');
        this.onChange();
    }
    get parent() {
        return this._parent;
    }
    set parent(parent) {
        if (this._parent === parent) {
            return;
        }
        this._parent = parent;
        this.onChangeParent();
    }
    forEachParents(callback) {
        let node = this.parent;
        let index = 0;
        while (node) {
            if (callback.call(this, node, index++)) {
                break;
            }
            node = node.parent;
        }
    }
    isParentOf(node) {
        let isParent = false;
        node.forEachParents((parent) => {
            if (parent === this) {
                isParent = true;
                return true;
            }
            return false;
        });
        return isParent;
    }
    get parentsCount() {
        let count = 0;
        this.forEachParents((parent) => {
            count++;
        });
        return count;
    }
    onChangeData() {
        this._events.emit('change-data');
        this._events.emit('change');
    }
    get data() {
        return this._data;
    }
    set data(data) {
        if (this._data === data) {
            return;
        }
        this._data = data;
        this.onChangeData();
    }
    clone() {
        var _a;
        const cloneData = ((_a = this._options) === null || _a === void 0 ? void 0 : _a.cloneData) || deepClone;
        const node = new TreeListNode(cloneData(this.data), deepClone(this._options));
        if (this._nodes) {
            this._nodes.forEach(o => {
                node.push(o.clone());
            });
        }
        return node;
    }
}
export function treeListNodeToObject(node, createObject) {
    const children = node.length
        ? node.toArray().map((n) => treeListNodeToObject(n, createObject))
        : null;
    return createObject(node.data, children);
}
export function objectToTreeListNode(object, getChildren, getData) {
    const node = new TreeListNode(getData(object));
    const children = getChildren(object);
    if (children) {
        children.forEach((o) => {
            node.insert(node.length, objectToTreeListNode(o, getChildren, getData));
        });
    }
    return node;
}
